import { motion } from 'framer-motion';
import {
  UserAvatarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@/config/icons';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { Agent } from '@/hooks/useSecondarySidebarData';

interface AgentsSectionProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loading: boolean;
  agents: Agent[];
  unassignedCount: number;
  currentAgentId: string | null;
  onAgentClick: (agentId: string | null) => void;
}

function getAgentName(agent: Agent) {
  if (agent.first_name && agent.last_name) {
    return `${agent.first_name} ${agent.last_name}`;
  }
  if (agent.first_name) return agent.first_name;
  if (agent.last_name) return agent.last_name;
  return 'Agent';
}

function getAgentInitials(agent: Agent) {
  if (agent.first_name && agent.last_name) {
    return `${agent.first_name[0]}${agent.last_name[0]}`.toUpperCase();
  }
  return 'AG';
}

export function AgentsSection({
  open,
  onOpenChange,
  loading,
  agents,
  unassignedCount,
  currentAgentId,
  onAgentClick,
}: AgentsSectionProps) {
  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <div className="flex items-center justify-between">
        <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
          <UserAvatarIcon className="h-4 w-4" />
          <span>AGENTS</span>
          {open ? (
            <ChevronUpIcon className="h-3 w-3" />
          ) : (
            <ChevronDownIcon className="h-3 w-3" />
          )}
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent className="mt-2 space-y-0.5">
        {loading ? (
          <motion.div
            className="space-y-1 px-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05, duration: 0.2 }}
                className="flex items-center gap-2 py-1"
              >
                <Skeleton className="h-5 w-5 rounded-full" />
                <Skeleton className="h-4 flex-1 rounded" />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <>
            {/* Unassigned tickets */}
            <button
              onClick={() => onAgentClick('unassigned')}
              className={cn(
                "w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors",
                currentAgentId === 'unassigned'
                  ? "bg-muted text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
            >
              <span className="flex items-center gap-2 truncate">
                <div className="w-5 h-5 rounded-full bg-muted-foreground/20 flex items-center justify-center">
                  <UserAvatarIcon className="h-3 w-3 text-muted-foreground" />
                </div>
                <span className="truncate">Non assignés</span>
              </span>
              <span className="text-xs opacity-60">{unassignedCount}</span>
            </button>

            {/* Agents */}
            {agents.map((agent) => (
              <button
                key={agent.id}
                onClick={() => onAgentClick(agent.user_id)}
                className={cn(
                  "w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors",
                  currentAgentId === agent.user_id
                    ? "bg-muted text-foreground font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )}
              >
                <span className="flex items-center gap-2 truncate">
                  {agent.avatar_url ? (
                    <img
                      src={agent.avatar_url}
                      alt={getAgentName(agent)}
                      className="w-5 h-5 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-medium">
                      {getAgentInitials(agent)}
                    </div>
                  )}
                  <span className="truncate">{getAgentName(agent)}</span>
                </span>
                <span className="text-xs opacity-60">{agent.ticket_count}</span>
              </button>
            ))}
          </>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
